import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  ListItemIcon
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoIcon from "@mui/icons-material/Info";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const OnboardingForm = () => {
  const [onboarding, setOnboarding] = useState({
    employee_id: "",
    candidateName: "",
    candidateEmail: "",
    onboardingProcess: "",
    processDetails: "",
    requiredDocuments: [],
    taskCompletionDate: "",
    uploadedFiles: [],
  });

  const [isCustomProcess, setIsCustomProcess] = useState(false);
  const [customProcessName, setCustomProcessName] = useState("");

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [documentOptions, setDocumentOptions] = useState([
    { value: "aadhar", label: "Aadhar Card" },
    { value: "pan", label: "PAN Card" },
    { value: "payslip", label: "Last Payslip" },
    { value: "form16", label: "Previous Employee Form 16" },
    { value: "relieving", label: "Relieving Letter" },
    { value: "education", label: "Educational Certificates" },
    { value: "bank", label: "Bank Account Details" },
    { value: "emergency", label: "Emergency Contact Information" },
  ]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.company_id) {
        axios.get(`${API_BASE_URL}/api/cmp/${payload.company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          if (res.data && res.data.data) {
            const country = res.data.data.country || "";
            const nationalIdLabel = res.data.data.nationalIdLabel || "PAN Card";
            
            setDocumentOptions(prev => {
              const newOptions = [...prev];
              const panIndex = newOptions.findIndex(o => o.value === "pan");
              if (panIndex !== -1) {
                newOptions[panIndex].label = nationalIdLabel;
              }
              if (country && country.toLowerCase() !== "india") {
                return newOptions.filter(o => o.value !== "aadhar");
              }
              return newOptions;
            });
          }
        }).catch(err =>
          { 
            // console.error("Error fetching company config:", err)
      });
      }
    } catch (e) {
      // console.error(e);
    }
  }, []);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    
    // Validate file types and sizes
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png', 'text/plain'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    const validFiles = newFiles.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: `File ${file.name} has invalid format. Please upload PDF, DOC, DOCX, JPG, JPEG, PNG, or TXT files only.`,
          severity: "error",
        });
        return false;
      }
      
      if (file.size > maxSize) {
        setSnackbar({
          open: true,
          message: `File ${file.name} is too large. Maximum size is 5MB.`,
          severity: "error",
        });
        return false;
      }
      
      return true;
    });

    if (validFiles.length > 0) {
      setOnboarding((prevState) => ({
        ...prevState,
        uploadedFiles: [...prevState.uploadedFiles, ...validFiles],
      }));
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setOnboarding((prevState) => ({
      ...prevState,
      uploadedFiles: prevState.uploadedFiles.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleDocumentChange = (event) => {
    const {
      target: { value },
    } = event;
    setOnboarding((prev) => ({
      ...prev,
      requiredDocuments: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!onboarding.employee_id) {
      setSnackbar({ open: true, message: "Please enter an employee ID", severity: "error" });
      return;
    }
    if (!onboarding.candidateName || !onboarding.candidateEmail) {
      setSnackbar({ open: true, message: "Please enter candidate name and email", severity: "error" });
      return;
    }
    if (!onboarding.onboardingProcess) {
      setSnackbar({ open: true, message: "Please select an onboarding process", severity: "error" });
      return;
    }
    if (onboarding.onboardingProcess === "custom" && !customProcessName.trim()) {
      setSnackbar({ open: true, message: "Please enter a custom onboarding process name", severity: "error" });
      return;
    }
    if (!onboarding.taskCompletionDate) {
      setSnackbar({ open: true, message: "Please select a completion date", severity: "error" });
      return;
    }
  
    setLoading(true);
  
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      setLoading(false);
      return;
    }
    const payloadToken = JSON.parse(atob(token.split('.')[1]));
    const company_id = payloadToken.company_id;
  
    try {
      const formData = new FormData();
      formData.append("employee_id", onboarding.employee_id);
      formData.append("candidateName", onboarding.candidateName);
      formData.append("candidateEmail", onboarding.candidateEmail);
      formData.append("onboardingProcess", onboarding.onboardingProcess === "custom" ? customProcessName.trim() : onboarding.onboardingProcess);
      formData.append("processDetails", onboarding.processDetails);
      formData.append("requiredDocuments", JSON.stringify(onboarding.requiredDocuments));
      formData.append("taskCompletionDate", onboarding.taskCompletionDate);
      formData.append("status", "Pending");
  
      if (onboarding.uploadedFiles.length > 0) {
        onboarding.uploadedFiles.forEach(file => {
          formData.append("uploadedFiles", file);
        });
      }
  

      // Append company_id as query parameter
      const response = await axios.post(`${API_BASE_URL}/api/onboarding`, formData, {
        headers: { 'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
         },
        timeout: 30000
      });
  
      setSnackbar({
        open: true,
        message: `Onboarding process assigned successfully! Email sent to ${onboarding.candidateEmail}`,
        severity: "success",
      });
  
      setOnboarding({
        employee_id: "",
        candidateName: "",
        candidateEmail: "",
        onboardingProcess: "",
        processDetails: "",
        requiredDocuments: [],
        taskCompletionDate: "",
        uploadedFiles: [],
      });
      setCustomProcessName("");
      setIsCustomProcess(false);
  
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
  
    } catch (err) {
      //console.error("Submission Error:", err);
  
      let errorMessage = "Something went wrong";
      if (err.code === 'ECONNABORTED') {
        errorMessage = "Request timeout. Please try again with smaller files.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
  
      setSnackbar({
        open: true,
        message: `Error: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <div style={{ minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1500, p: 3, borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4, mx: 3 }}>
        <Card>
          <Typography variant="h4" gutterBottom  fontWeight="bold" >
            Onboarding Process
          </Typography>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Tooltip title="Enter the unique ID assigned to the hiring manager or HR personnel creating this onboarding process" arrow placement="top">
                    <TextField
                      label="Employee ID"
                      value={onboarding.employee_id}
                      onChange={(e) =>
                        setOnboarding((prev) => ({ ...prev, employee_id: e.target.value }))
                      }
                      fullWidth
                      required
                      margin="normal"
                    />
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Candidate Name"
                    value={onboarding.candidateName}
                    onChange={(e) =>
                      setOnboarding((prev) => ({ ...prev, candidateName: e.target.value }))
                    }
                    fullWidth
                    required
                    margin="normal"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Candidate Email"
                    type="email"
                    value={onboarding.candidateEmail}
                    onChange={(e) =>
                      setOnboarding((prev) => ({ ...prev, candidateEmail: e.target.value }))
                    }
                    fullWidth
                    required
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Completion Date"
                    type="date"
                    value={onboarding.taskCompletionDate}
                    onChange={(e) =>
                      setOnboarding((prev) => ({ ...prev, taskCompletionDate: e.target.value }))
                    }
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Tooltip title="Select the specific onboarding stage to assign to this candidate" arrow placement="top">
                    <FormControl fullWidth required margin="normal">
                      <InputLabel>Onboarding Process</InputLabel>
                      <Select
                        value={onboarding.onboardingProcess}
                        label="Onboarding Process"
                        onChange={(e) => {
                          const val = e.target.value;
                          setOnboarding((prev) => ({ ...prev, onboardingProcess: val }));
                          if (val === "custom") {
                            setIsCustomProcess(true);
                          } else {
                            setIsCustomProcess(false);
                            setCustomProcessName("");
                          }
                        }}
                      >
                        <MenuItem value="document-verification">Document Verification</MenuItem>
                        <MenuItem value="training">Mandatory Training</MenuItem>
                        <MenuItem value="induction">Induction Meetings</MenuItem>
                        <MenuItem value="complete-onboarding">Complete Onboarding Process</MenuItem>
                        <MenuItem value="custom">Other (Custom)</MenuItem>
                      </Select>
                    </FormControl>
                  </Tooltip>
                </Grid>
              </Grid>

              {isCustomProcess && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Custom Onboarding Process"
                      value={customProcessName}
                      onChange={(e) => setCustomProcessName(e.target.value)}
                      fullWidth
                      required
                      margin="normal"
                      placeholder="Enter custom onboarding process name"
                    />
                  </Grid>
                </Grid>
              )}

              <Tooltip title="Add specific instructions, meeting links, or detailed requirements for the candidate to complete this stage" arrow placement="top">
                <TextField
                  label="Process Details"
                  value={onboarding.processDetails}
                  onChange={(e) =>
                    setOnboarding((prev) => ({ ...prev, processDetails: e.target.value }))
                  }
                  fullWidth
                  required
                  margin="normal"
                  multiline
                  rows={3}
                  placeholder="Provide details about what the candidate needs to do"
                />
              </Tooltip>

              <Grid item xs={12}>
                <Tooltip title="Select all documents that the candidate must submit. They will receive an email with this checklist." arrow placement="top">
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Required Documents </InputLabel>
                    <Select
                      multiple
                      value={onboarding.requiredDocuments}
                      label="Required Documents "
                      onChange={handleDocumentChange}
                      renderValue={(selected) =>
                        selected
                          .map((value) => documentOptions.find((option) => option.value === value)?.label)
                          .join(', ')
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            marginTop: 138,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {documentOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <ListItemIcon>
                            <Checkbox 
                              checked={onboarding.requiredDocuments.indexOf(option.value) > -1}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText primary={option.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>

                <Box sx={{ mt: 2 }}>
                  <Tooltip title="Upload any reference materials or templates that the candidate will need to complete this process (Optional)" arrow placement="top">
                    <Button variant="outlined" component="label">
                      Upload Additional Files (Optional)
                      <input 
                        type="file" 
                        hidden 
                        onChange={handleFileChange} 
                        multiple 
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                      />
                    </Button>
                  </Tooltip>

                  {onboarding.uploadedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body1">Uploaded Files:</Typography>
                      <List dense>
                        {onboarding.uploadedFiles.map((file, index) => (
                          <ListItem
                            key={index}
                            secondaryAction={
                              <IconButton edge="end" onClick={() => handleRemoveFile(index)}>
                                <DeleteIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText 
                              primary={file.name} 
                              secondary={`Size: ${(file.size / 1024).toFixed(2)} KB | Type: ${file.type}`} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  sx={{ marginTop: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Assign Onboarding Process"}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OnboardingForm;